import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, X } from 'lucide-react';

const uid = () => Math.random().toString(36).slice(2);

const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * MultiDatePicker
 *
 * Props:
 *   value: [{ id, date, time }]  — controlled
 *   onChange: (dates) => void
 *   defaultTime: string           — pre-fill time for new rows
 */
export default function MultiDatePicker({ value, onChange, defaultTime = '' }) {
  const today = new Date().toLocaleDateString('sv');

  const [mode, setMode]         = useState('single');   // 'single' | 'range' | 'individual'
  const [rangeFrom, setRangeFrom] = useState(today);
  const [rangeTo, setRangeTo]     = useState(today);
  const [rangeTime, setRangeTime] = useState(defaultTime);

  // ── Switch to Single ────────────────────────────────────────────────────────
  const switchToSingle = () => {
    setMode('single');
    // Keep only the first entry (or default)
    const first = value[0] || { id: uid(), date: today, time: defaultTime };
    onChange([{ ...first }]);
  };

  // ── Switch to Multiple ──────────────────────────────────────────────────────
  const switchToMultiple = (subMode) => {
    setMode(subMode);
    if (subMode === 'individual') {
      // Seed with two empty rows so the user has something to fill in
      onChange([
        { id: uid(), date: value[0]?.date || today, time: value[0]?.time || defaultTime },
        { id: uid(), date: today, time: defaultTime },
      ]);
    }
    // For 'range', let the range inputs drive the list
  };

  // ── Range auto-generate ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'range' || !rangeFrom || !rangeTo) return;
    const from = new Date(rangeFrom + 'T12:00:00');
    const to   = new Date(rangeTo   + 'T12:00:00');
    if (from > to) return;
    const days = [];
    let cur = new Date(from);
    while (cur <= to && days.length < 366) {
      days.push({ id: uid(), date: cur.toLocaleDateString('sv'), time: rangeTime });
      cur.setDate(cur.getDate() + 1);
    }
    onChange(days);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rangeFrom, rangeTo, rangeTime]);

  // ── Single helpers ──────────────────────────────────────────────────────────
  const setSingleDate = (d) => onChange([{ ...value[0], date: d }]);
  const setSingleTime = (t) => onChange([{ ...value[0], time: t }]);

  // ── Individual helpers ──────────────────────────────────────────────────────
  const addRow    = ()        => onChange([...value, { id: uid(), date: today, time: defaultTime }]);
  const removeRow = (id)      => { if (value.length > 1) onChange(value.filter(r => r.id !== id)); };
  const updateRow = (id, k, v) => onChange(value.map(r => r.id === id ? { ...r, [k]: v } : r));

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      {/* Mode toggle */}
      <div className="flex gap-1">
        {['Single Date', 'Multiple Dates'].map((label, i) => (
          <button key={i} type="button"
            onClick={() => i === 0 ? switchToSingle() : switchToMultiple(mode === 'single' ? 'range' : mode)}
            className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
              (i === 0) === (mode === 'single')
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── SINGLE ── */}
      {mode === 'single' && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Date</Label>
            <Input type="date" value={value[0]?.date || today}
              onChange={e => setSingleDate(e.target.value)}
              className="time-filled h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Time</Label>
            <Input type="time" value={value[0]?.time || ''}
              onChange={e => setSingleTime(e.target.value)}
              className={`h-8 text-xs ${value[0]?.time ? 'time-filled' : ''}`} />
          </div>
        </div>
      )}

      {/* ── MULTIPLE: sub-mode toggle ── */}
      {mode !== 'single' && (
        <>
          <div className="flex gap-1">
            {['Date Range', 'Individual Dates'].map((label, i) => {
              const sub = i === 0 ? 'range' : 'individual';
              return (
                <button key={i} type="button"
                  onClick={() => switchToMultiple(sub)}
                  className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                    mode === sub
                      ? 'bg-sky-100 text-sky-700 border-sky-300'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}>
                  {label}
                </button>
              );
            })}
          </div>

          {/* ── RANGE ── */}
          {mode === 'range' && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">From</Label>
                  <Input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
                    className="time-filled h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">To</Label>
                  <Input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)}
                    className="time-filled h-8 text-xs" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Default Time (applies to all)</Label>
                <Input type="time" value={rangeTime} onChange={e => setRangeTime(e.target.value)}
                  className={`h-8 text-xs ${rangeTime ? 'time-filled' : ''}`} />
              </div>
              {value.length > 0 && (
                <div className="border rounded max-h-44 overflow-y-auto">
                  <div className="sticky top-0 bg-muted/20 border-b px-2 py-1 text-xs text-muted-foreground font-medium">
                    {value.length} date(s) — override individual times below
                  </div>
                  {value.map((row) => (
                    <div key={row.id} className="flex items-center gap-2 px-2 py-1 border-b last:border-0">
                      <span className="text-xs flex-1 font-medium">{fmtDate(row.date)}</span>
                      <Input type="time" value={row.time || ''}
                        onChange={e => updateRow(row.id, 'time', e.target.value)}
                        className={`h-6 text-[10px] w-24 ${row.time ? 'time-filled' : ''}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── INDIVIDUAL ── */}
          {mode === 'individual' && (
            <div className="space-y-1.5">
              {value.map((row) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Input type="date" value={row.date}
                    onChange={e => updateRow(row.id, 'date', e.target.value)}
                    className="time-filled h-8 text-xs flex-1" />
                  <Input type="time" value={row.time || ''}
                    onChange={e => updateRow(row.id, 'time', e.target.value)}
                    className={`h-8 text-xs w-28 ${row.time ? 'time-filled' : ''}`} />
                  <button type="button" onClick={() => removeRow(row.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs w-full"
                onClick={addRow}>
                <Plus className="w-3 h-3 mr-1" /> Add Date
              </Button>
            </div>
          )}
        </>
      )}

      {mode !== 'single' && value.length > 1 && (
        <p className="text-xs text-muted-foreground">
          {value.length} entries will be created — one per date.
        </p>
      )}
    </div>
  );
}
